// @flow

// 자막(Message) 전송은 전부 이 모듈을 거친다 (#181).
// - 한 줄로 줄 세워 앞 요청이 끝난 뒤 다음을 보낸다 → 표시 순서가 Enter 순서와 같다
// - 실패하면 같은 id로 재시도한다. createMessage 리졸버가 attribute_not_exists(id) 조건을 걸어서,
//   첫 요청이 실제로는 성공했더라도 재시도가 중복 자막을 만들지 않는다
// - 재시도 시간을 넘기면 실패 목록에 남겨 번역자가 보고 다시 보낼 수 있게 한다

import { API, Auth } from "aws-amplify";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { createMessage as createMessageMutation } from "../graphql/mutations";
import { DEFAULT_MESSAGE_TYPE } from "./util";

// 서울 직결 전송 경로 (#183, infra/send-relay/template.yaml).
// AppSync 기본 엔드포인트는 CloudFront 엣지를 거쳐 해외에서 서울까지 왕복이 2~3번 생긴다.
// REGIONAL API로 보내면 미국에서 약 0.6초 → 0.3초. 실패하면 같은 시도 안에서 기본 경로로 폴백한다.
// 세션의 Broadcast Settings에서 켠다 (BroadcastSetting.useSendRelay, 번역 화면이 구독으로 바로 반영).
// 비상용 덮어쓰기: 주소에 ?relay=on / ?relay=off 가 있는 동안만 세션 설정 대신 쓴다 (기억하지 않음).
// 예전엔 localStorage에 기억했는데, 켠 걸 잊으면 설정을 안 켠 세션에도 적용돼 헷갈려서 바꿨다.
let sendRelayUrl = "https://lvzwji67g2.execute-api.ap-northeast-2.amazonaws.com/messages";
const RELAY_TIMEOUT_MS = 1500;
const RELAY_STORAGE_KEY = "sendRelay";

const ATTEMPT_TIMEOUT_MS = 4000;
const RETRY_WINDOW_MS = 12000;
const BACKOFF_MS = [300, 1000, 2000, 3000];
const SLOW_SEND_MS = 800;

type QueueItem = {
  key: string,
  input: Object,
  context: string,
  enqueuedAt: number,
};

export type SendStatus = {
  pendingCount: number,
  retrying: boolean,
  failed: Array<{ key: string, text: string }>,
};

const queue: Array<QueueItem> = [];
const failed: Array<QueueItem> = [];
const listeners: Set<(SendStatus) => void> = new Set();
let draining = false;
let headRetrying = false;

function newId(): string {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getStatus(): SendStatus {
  return {
    pendingCount: queue.length,
    retrying: headRetrying,
    failed: failed.map((item) => ({ key: item.key, text: item.input.text })),
  };
}

function notify() {
  const status = getStatus();
  listeners.forEach((listener) => listener(status));
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms)
    ),
  ]);
}

// 앞선 시도가 이미 저장된 경우 — 성공으로 본다
function isAlreadyStored(err): boolean {
  return (err?.errors ?? []).some((e) =>
    String(e?.errorType ?? "").includes("ConditionalCheckFailed")
  );
}

// 테스트 전용
export function setSendRelayUrlForTest(url: string) {
  sendRelayUrl = url;
}

let sessionRelayEnabled = false;
const relayListeners: Set<() => void> = new Set();

// 번역 화면이 세션의 BroadcastSetting.useSendRelay 값을 넘긴다
export function setSessionRelayEnabled(enabled: boolean) {
  if (sessionRelayEnabled === enabled) {
    return;
  }
  sessionRelayEnabled = enabled;
  relayListeners.forEach((listener) => listener());
}

// 예전 버전이 기억해 둔 값을 지운다
try {
  window.localStorage.removeItem(RELAY_STORAGE_KEY);
} catch (e) {}

function readRelayOverride(): ?string {
  try {
    const param = new URLSearchParams(window.location.search).get("relay");
    return param === "on" || param === "off" ? param : null;
  } catch (e) {
    return null;
  }
}

export function isRelayEnabled(): boolean {
  if (sendRelayUrl === "") {
    return false;
  }
  const override = readRelayOverride();
  if (override === "on" || override === "off") {
    return override === "on";
  }
  return sessionRelayEnabled;
}

async function postToRelay(body: Object): Promise<Response> {
  const session = await Auth.currentSession();
  return fetch(sendRelayUrl, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: session.getIdToken().getJwtToken(),
    },
    body: JSON.stringify(body),
  });
}

// GraphQL 오류({ errors })는 AppSync가 판단한 결과라 그대로 올린다 (중복 저장 판정 포함).
// 그 외(네트워크, 시간 초과, 인증·서버 오류)는 기본 경로로 다시 보낸다.
async function sendViaRelay(input: Object): Promise<void> {
  let json = null;
  try {
    const res = await withTimeout(postToRelay(input), RELAY_TIMEOUT_MS);
    json = await res.json().catch(() => null);
    if (!res.ok && !Array.isArray(json?.errors)) {
      throw new Error(`relay HTTP ${res.status}`);
    }
  } catch (err) {
    await API.graphql({ query: createMessageMutation, variables: { input } });
    return;
  }
  if (Array.isArray(json?.errors) && json.errors.length > 0) {
    throw json;
  }
}

function sendOnce(input: Object): Promise<mixed> {
  if (isRelayEnabled()) {
    return sendViaRelay(input);
  }
  return API.graphql({ query: createMessageMutation, variables: { input } });
}

async function sendWithRetry(item: QueueItem): Promise<any> {
  const start = Date.now();
  let lastError = null;
  for (let attempt = 0; ; attempt++) {
    try {
      await withTimeout(sendOnce(item.input), ATTEMPT_TIMEOUT_MS);
      return null;
    } catch (err) {
      if (isAlreadyStored(err)) {
        return null;
      }
      lastError = err;
      const backoff = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      if (Date.now() - start + backoff > RETRY_WINDOW_MS) {
        return lastError;
      }
      headRetrying = true;
      notify();
      await new Promise((resolve) => setTimeout(resolve, backoff));
    }
  }
}

async function drain() {
  if (draining) {
    return;
  }
  draining = true;
  while (queue.length > 0) {
    const item = queue[0];
    const error = await sendWithRetry(item);
    queue.shift();
    headRetrying = false;
    const duration = Date.now() - item.enqueuedAt;
    if (error) {
      failed.push(item);
      const message =
        error?.errors?.[0]?.message ?? error?.message ?? "unknown error";
      Sentry.captureException(new Error(`createMessage failed after retries: ${message}`), {
        extra: { context: item.context, sessionId: item.input.sessionMessagesId, duration, rawError: error },
      });
    } else if (duration > SLOW_SEND_MS) {
      Sentry.captureMessage(`Send slow: ${duration}ms`, {
        level: "warning",
        extra: { context: item.context, sessionId: item.input.sessionMessagesId, duration },
      });
    }
    notify();
  }
  draining = false;
}

export function sendMessage({
  sessionId,
  text,
  scriptIndex,
  username,
  context,
}: {
  sessionId: string,
  text: string,
  scriptIndex?: number,
  username?: string,
  context: string,
}) {
  if (sessionId === "") {
    return;
  }
  const id = newId();
  const input: Object = {
    id,
    sessionMessagesId: sessionId,
    text,
    type: DEFAULT_MESSAGE_TYPE,
  };
  if (scriptIndex != null) {
    input.scriptIndex = scriptIndex;
  }
  if (username != null) {
    input.username = username;
  }
  queue.push({ key: id, input, context, enqueuedAt: Date.now() });
  notify();
  drain();
}

// 실패한 줄을 같은 id로 다시 줄 세운다 (이미 저장됐다면 중복 없이 성공 처리된다)
export function resendFailed(key: string) {
  const index = failed.findIndex((item) => item.key === key);
  if (index === -1) {
    return;
  }
  const [item] = failed.splice(index, 1);
  queue.push({ ...item, enqueuedAt: Date.now() });
  notify();
  drain();
}

export function dismissFailed(key: string) {
  const index = failed.findIndex((item) => item.key === key);
  if (index !== -1) {
    failed.splice(index, 1);
    notify();
  }
}

// 직결 경로를 쓸 때 연결과 Lambda가 식지 않게 주기적으로 가벼운 요청을 보낸다.
// 빈 본문은 Lambda가 204로 바로 돌려보낸다 (저장되는 것 없음)
export function useRelayEnabled(): boolean {
  const [enabled, setEnabled] = useState(isRelayEnabled);
  useEffect(() => {
    const listener = () => setEnabled(isRelayEnabled());
    relayListeners.add(listener);
    listener();
    return () => {
      relayListeners.delete(listener);
    };
  }, []);
  return enabled;
}

// 서울 relay까지 왕복 시간(ms)을 잰다. 연결 상태 표시등(#204)의 확인 요청이자 Lambda를 데워 두는 역할도 한다
// (예전 Direct Send keepalive를 대신한다). HTTP 응답이 오기만 하면 네트워크는 살아 있는 것으로 보고,
// 네트워크 오류·시간 초과면 reject한다.
export async function pingRelay(timeoutMs: number): Promise<number> {
  const started = Date.now();
  await withTimeout(postToRelay({}), timeoutMs);
  return Date.now() - started;
}

export function useSendStatus(): SendStatus {
  const [status, setStatus] = useState(getStatus);
  useEffect(() => {
    listeners.add(setStatus);
    setStatus(getStatus());
    return () => {
      listeners.delete(setStatus);
    };
  }, []);
  return status;
}
